import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { Section } from "@/components/Section";
import SmartImage from "@/components/SmartImage";
import { ShareButton } from "@/components/ShareButton";

export default function AthleteDetail() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setA(null); setMissing(false);
    api.get(`/athletes/${slug}`).then((r) => { setA(r.data); document.title = `${r.data.name} — Ironveil Era`; }).catch(() => setMissing(true));
    return () => { document.title = "Ironveil Era"; };
  }, [slug]);

  if (missing) return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center px-5 text-center" data-testid="athlete-not-found">
      <div className="type-label text-[#C8102E] mb-3">Athlete not found</div>
      <h1 className="type-h2">This profile does not exist.</h1>
      <Link to="/records" className="btn-secondary mt-8"><ArrowLeft size={14} /> Back to records</Link>
    </div>
  );
  if (!a) return <div className="min-h-[60vh] flex items-center justify-center text-[#888]">Loading…</div>;

  const finishes = a.ko || 0;
  const subs = a.submissions || 0;
  const decisions = Math.max((a.wins || 0) - finishes - subs, 0);
  const history = a.fight_history || [];

  return (
    <div data-testid="athlete-detail-page">
      <section className="relative overflow-hidden bg-black border-b border-[#151515]">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 pt-24 lg:pt-28 pb-16 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-4 relative aspect-[3/4] border border-[#2a2a2a] max-w-sm">
            <SmartImage path={a.photo_path} fallback="fighter" alt={a.name} className="w-full h-full object-cover" />
            <div className="absolute top-4 left-4 bg-[#C8102E] text-white type-meta px-3 py-1.5">{a.level} · {a.sport}</div>
          </div>
          <div className="lg:col-span-8">
            <div className="flex items-center justify-between gap-4 mb-6">
              <Link to="/records" className="btn-text text-[#a0a0a0] inline-flex" data-testid="athlete-back"><ArrowLeft size={14} /> All records</Link>
              <ShareButton title={`${a.name} — Ironveil Era`} text={`${a.name} · ${a.sport} ${a.level} · ${a.weight_class} · ${a.wins}-${a.losses}-${a.draws}. Athlete profile on Ironveil Era.`} />
            </div>
            <div className="type-label text-[#C8102E] mb-3">{a.weight_class}{a.ranking ? ` · Ranked #${a.ranking}` : ""}</div>
            <h1 className="type-h1" data-testid="athlete-name">{a.name}</h1>
            {a.bio && <p className="mt-6 text-[#c9c9c9] text-lg max-w-2xl">{a.bio}</p>}
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="athlete-stats">
              <Stat label={`${a.sport} ${a.level} record`} value={`${a.wins}–${a.losses}–${a.draws}`} big />
              <Stat label={a.sport === "Boxing" ? "KO wins" : "KO / TKO wins"} value={finishes} />
              <Stat label="Submissions" value={subs} />
              <Stat label="Decisions" value={decisions} />
            </div>
          </div>
        </div>
      </section>

      <Section testId="athlete-details" eyebrow="Fighter details" title="Profile">
        <dl className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#2a2a2a] border border-[#2a2a2a]">
          <Detail k="Sport" v={a.sport} />
          <Detail k="Division" v={a.weight_class} />
          <Detail k="Level" v={a.level} />
          <Detail k="Height" v={a.height} />
          <Detail k="Affiliation" v={a.academy} />
          <Detail k="Coach" v={a.coach} />
          <Detail k="Fighting out of" v={[a.city, a.state].filter(Boolean).join(", ") || "India"} />
          <Detail k="Reach" v={a.reach} />
          <Detail k="Last weigh-in" v={a.last_weigh_in} />
        </dl>
      </Section>

      <Section testId="athlete-history" eyebrow={`${a.sport} fight history`} title="Fight History">
        {history.length ? (
          <div className="card-ive divide-y divide-[#2a2a2a]" data-testid="fight-history">
            <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 type-meta text-[#666]">
              <div className="col-span-2">Date</div><div className="col-span-3">Event</div><div className="col-span-3">Opponent</div><div className="col-span-1">Result</div><div className="col-span-3">Method</div>
            </div>
            {history.map((h, i) => (
              <div key={i} className="grid grid-cols-12 gap-x-3 gap-y-1 px-5 py-4 text-sm items-center" data-testid={`fight-row-${i}`}>
                <div className="col-span-6 md:col-span-2 text-xs text-[#a0a0a0] order-1">{h.date}</div>
                <div className="col-span-6 md:col-span-3 font-display text-xl order-3 md:order-2">{h.event}</div>
                <div className="col-span-12 md:col-span-3 order-4 md:order-3">vs {h.opponent}</div>
                <div className={`col-span-4 md:col-span-1 font-display order-2 md:order-4 text-right md:text-left ${h.result === "Win" ? "text-[#C8102E]" : h.result === "Loss" ? "text-[#888]" : "text-white"}`}>{h.result}</div>
                <div className="col-span-12 md:col-span-3 text-xs text-[#a0a0a0] order-5">{h.method}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="card-ive p-10 text-center text-[#888]">No bouts recorded yet.</div>
        )}
        <div className="mt-10 flex flex-col sm:flex-row gap-3">
          <Link to="/records" className="btn-secondary"><ArrowLeft size={14} /> Back to records</Link>
          <Link to="/register" className="btn-primary" data-testid="athlete-register-cta">Compete with Ironveil Era <ArrowRight size={14} /></Link>
        </div>
      </Section>
    </div>
  );
}

function Stat({ label, value, big }) {
  return (
    <div className="card-ive p-5">
      <div className="type-meta text-[#888]">{label}</div>
      <div className={`type-stat mt-2 ${big ? "text-[#C8102E]" : ""}`}>{value}</div>
    </div>
  );
}

function Detail({ k, v }) {
  return (
    <div className="bg-[#0d0d0d] p-5">
      <dt className="type-meta text-[#888]">{k}</dt>
      <dd className="mt-1 text-sm">{v || "N/A"}</dd>
    </div>
  );
}
